import { useCallback, useEffect, useRef, useState } from 'react';
import { apiPost } from '../../lib/api';
import { flashToast } from './helpers';

/* Storage keys (mirror Reflection.js) */
const failedKey = (uid) => `failed_reflections-${uid || 'me'}`;
const failedApplyKey = (uid) => `failed_apply_changes-${uid || 'me'}`;

/**
 * Offline retry queues for Reflection.
 *  - failed-ingest queue: journal entries that failed to POST /api/journal/ingest
 *  - failed-apply queue: pending-change applies that failed to POST .../apply
 *
 * Returns:
 *   {
 *     queueLength,        // number of pending failed ingests (drives the badge)
 *     drain,              // re-attempt failed-ingest queue (no-op offline)
 *     drainApply,         // re-attempt failed-apply queue  (no-op offline)
 *     queueFailed,        // enqueue a failed ingest payload
 *     queueFailedApply,   // enqueue a failed apply by change_id
 *   }
 *
 * Also:
 *   - drains both queues on mount (once per mount, guarded)
 *   - re-drains both when the browser comes back online
 */
export function useApplyQueue(userId) {
  const [queueLength, setQueueLength] = useState(() => {
    try {
      const q = JSON.parse(localStorage.getItem(failedKey(userId)) || '[]');
      return Array.isArray(q) ? q.length : 0;
    } catch (_) { return 0; }
  });

  const retryRanRef = useRef(false);

  /* drain failed-ingest queue (with max retries + badge update) */
  const drain = useCallback(async () => {
    if (!navigator.onLine) return;
    const k = failedKey(userId);
    let queue;
    try { queue = JSON.parse(localStorage.getItem(k) || '[]'); } catch (_) { return; }
    if (!Array.isArray(queue) || queue.length === 0) return;

    const remaining = [];
    for (const entry of queue) {
      if ((entry._retries ?? 0) >= 3) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('Dropping reflection after 3 failed retries', entry._t);
        }
        continue;
      }
      try {
        const r = await apiPost('/api/journal/ingest', { ...entry });
        if (!r.ok) remaining.push({ ...entry, _retries: (entry._retries ?? 0) + 1 });
      } catch (_) {
        remaining.push({ ...entry, _retries: (entry._retries ?? 0) + 1 });
      }
    }
    try { localStorage.setItem(k, JSON.stringify(remaining)); } catch (_) {}
    setQueueLength(remaining.length);
  }, [userId]);

  /* drain failed-apply queue (offline confirms) */
  const drainApply = useCallback(async () => {
    if (!navigator.onLine) return;
    const k = failedApplyKey(userId);
    let queue;
    try { queue = JSON.parse(localStorage.getItem(k) || '[]'); } catch (_) { return; }
    if (!Array.isArray(queue) || queue.length === 0) return;
    const remaining = [];
    let droppedCount = 0;
    for (const entry of queue) {
      if ((entry._retries ?? 0) >= 3) {
        // 3 retries failed — surface to the user instead of silently
        // dropping. The user thought they confirmed; tell them the truth.
        droppedCount += 1;
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('Dropping queued apply after 3 failed retries', entry.change_id);
        }
        continue;
      }
      try {
        const r = await apiPost(`/api/pending-changes/${entry.change_id}/apply`, {});
        // 404/409 = already handled — count as success, drop without warning.
        if (r && (r.ok || r.status === 404 || r.status === 409)) {
          // resolved
        } else {
          remaining.push({ ...entry, _retries: (entry._retries ?? 0) + 1 });
        }
      } catch (_) {
        remaining.push({ ...entry, _retries: (entry._retries ?? 0) + 1 });
      }
    }
    try { localStorage.setItem(k, JSON.stringify(remaining)); } catch (_) {}
    if (droppedCount > 0) {
      flashToast(
        droppedCount === 1
          ? "One change couldn't be applied — please try adding it again."
          : `${droppedCount} changes couldn't be applied — please try adding them again.`,
        'error',
      );
    }
  }, [userId]);

  const queueFailed = useCallback((payload) => {
    try {
      const k = failedKey(userId);
      const arr = JSON.parse(localStorage.getItem(k) || '[]');
      arr.push({ ...payload, _t: Date.now() });
      localStorage.setItem(k, JSON.stringify(arr));
      setQueueLength(arr.length);
    } catch (_) { /* ignore */ }
  }, [userId]);

  const queueFailedApply = useCallback((change_id) => {
    try {
      const k = failedApplyKey(userId);
      const arr = JSON.parse(localStorage.getItem(k) || '[]');
      arr.push({ change_id, _t: Date.now() });
      localStorage.setItem(k, JSON.stringify(arr));
    } catch (_) { /* ignore */ }
  }, [userId]);

  /* Drain on mount + when coming back online. Logged-out (userId == null) skips. */
  useEffect(() => {
    if (userId == null) return;

    if (!retryRanRef.current) {
      retryRanRef.current = true;
      drain();
      drainApply();
    }

    const onOnline = () => {
      retryRanRef.current = true;
      drain();
      drainApply();
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [userId, drain, drainApply]);

  return { queueLength, drain, drainApply, queueFailed, queueFailedApply };
}

export default useApplyQueue;
